/**
 * The client's MachForms DSR link(s) on the Chase queue (Tracey, 2026-10-30:
 * "needed when reminder emails are sent to the support workers"). Pure, so
 * the wording and de-duplication are testable without a DOM.
 *
 * Every DSR case carries the client's form ids (`formIds`, from app.dsr's
 * reconciler via omon-api). Shift-record cases have no MachForm, and an
 * older omon-api sends no `formIds` at all: both simply show no link.
 */
import { clientLabel, type FilterableRow } from "./filters";

/** The public MachForms view page; the form id is appended. The ONE place
 *  the planner names it (agent.chase/chase_worker/env.py holds the same
 *  base for the chase emails). */
export const MACHFORM_VIEW_URL = "https://sweettree-mf.co.uk/machform/view.php?id=";

export function formUrl(formId: string): string {
  return `${MACHFORM_VIEW_URL}${encodeURIComponent(formId)}`;
}

export interface FormLink {
  formId: string;
  url: string;
  /** "DSR form", or "DSR form 2" when a client has more than one. */
  label: string;
}

/** A row's form ids, tolerant of an older API (missing) and of junk. */
function idsOf(r: { formIds?: unknown }): string[] {
  if (!Array.isArray(r.formIds)) return [];
  const out: string[] = [];
  for (const v of r.formIds) {
    const id = typeof v === "number" || typeof v === "string" ? String(v).trim() : "";
    if (id && !out.includes(id)) out.push(id);
  }
  return out;
}

/** The links for one case row: one per form id, in the API's order. */
export function formLinksOf(r: { formIds?: unknown }): FormLink[] {
  const ids = idsOf(r);
  return ids.map((formId, i) => ({
    formId,
    url: formUrl(formId),
    label: ids.length > 1 ? `DSR form ${i + 1}` : "DSR form",
  }));
}

export interface ClientFormLinks {
  client: string;
  links: FormLink[];
}

/** A carer card's links: each owed client once (with the label the queue
 *  already shows for them), each form once, first-seen order. */
export function carerFormLinks(rows: (FilterableRow & { formIds?: unknown })[]): ClientFormLinks[] {
  const by = new Map<string, ClientFormLinks>();
  const seen = new Set<string>();
  for (const r of rows) {
    const fresh = idsOf(r).filter((id) => !seen.has(id));
    if (!fresh.length) continue;
    fresh.forEach((id) => seen.add(id));
    const client = clientLabel(r);
    const entry = by.get(client) ?? { client, links: [] };
    entry.links.push(...fresh.map((formId) => ({ formId, url: formUrl(formId), label: "DSR form" })));
    by.set(client, entry);
  }
  for (const e of by.values()) {
    if (e.links.length > 1) e.links.forEach((l, i) => (l.label = `DSR form ${i + 1}`));
  }
  return [...by.values()];
}
